import { createPath } from 'history';
import { parse } from 'qs';
import { createElement, useContext, useEffect } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import { OrganizationDetailFragment } from '../../generated/graphql';
import mergeUrlQuery from '../../utility/mergeUrlQuery';
import { context as ConfigContext } from '../context/config';
import { context as DependencyContext } from '../context/dependency';
import { context as HistoryContext } from '../context/history';
import { context as StateContext } from '../context/state';
import { useClearRecoilState } from '../page/ProjectEditingPage/hooks/useClearRecoilState';
import Page from '../page/ShowProjectPage';

type RouteParams = {
  projectId: string;
  tabName: string;
};

type Props = RouteComponentProps<RouteParams>;

const ProjectDetailPage = ({ match }: Props) => {
  // Hooks
  const state = useContext(StateContext);
  const history = useContext(HistoryContext);
  const dependency = useContext(DependencyContext);
  const config = useContext(ConfigContext);
  const clearRecoilState = useClearRecoilState();

  const { comment: selectedProjectIssueId } = parse(state.location.search.slice(1));

  useEffect(() => {
    // In-Page Link
    const targetElement = document.getElementById(selectedProjectIssueId);

    if (targetElement) {
      const rectTop = targetElement.getBoundingClientRect().top;
      const offsetTop = window.pageYOffset;
      const headBarHeight = 60;
      const top = rectTop + offsetTop - headBarHeight;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    }
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.project.projectFetching === 0]);

  if (
    match.params.tabName !== 'proposal' &&
    match.params.tabName !== 'deliverables' &&
    match.params.tabName !== 'brief' &&
    match.params.tabName !== 'reporting'
  ) {
    return <Redirect to={`/projects/${match.params.projectId}/deliverables`} />;
  }

  const query = parse(state.location.search.slice(1));
  const organizationId = query.org;
  let organization: OrganizationDetailFragment | null = null;

  if (state.lastAccessedOrganization) {
    organization = state.lastAccessedOrganization;
  } else {
    organization = state.availableOrganizations.items[0];
  }

  // クエパラにorganizationIdが存在しない場合はリダイレクトする
  if (!organizationId && organization) {
    return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
  }

  const performanceReports = new Map(
    Array.from(state.projectReporting.performanceReports, ([k, value]) => [
      k,
      {
        ...value,
        dailySummaryPerformanceFetching: value.dailySummaryPerformanceFetching >= 1,
        periodicDetailPerformanceFetching: value.periodicDetailPerformanceFetching >= 1,
        periodicImprovementPerformanceFetching: value.periodicImprovementPerformanceFetching >= 1,
      },
    ]),
  );
  return (
    <Page
      tabName={match.params.tabName}
      project={state.project.project}
      basicProjectTypeIdByJp={config.BASIC_PROJECT_ID_BY_JP}
      basicProjectTypeIdByUs={config.BASIC_PROJECT_ID_BY_US}
      briefFiles={state.project.briefFiles}
      assetFiles={state.project.assetFiles}
      projectLoading={state.project.projectFetching >= 1}
      performanceReports={performanceReports}
      onReportRequestChange={(report, reportRequest) => {
        dependency.showPerformanceReportUsecase.setReportRequest(report, reportRequest);
      }}
      onSummaryRequestChange={(report, reportRequest) => {
        dependency.showPerformanceReportUsecase.prepareDailySummaryPerformance(report, reportRequest);
      }}
      hasLocalProjectData={state.project.hasLocalData}
      onClickEditProject={() => {
        clearRecoilState();
        dependency.showPerformanceReportUsecase.toEditPage();
      }}
      onRequestExportingPerformanceReport={(report, reportRequest, dimension) => {
        dependency.showPerformanceReportUsecase.requestExportingPerformanceReport(report, reportRequest, dimension);
      }}
      onIntegrationBannerClose={(adPlatform) => {
        dependency.showPerformanceReportUsecase.hideIntegrationBanner(adPlatform);
      }}
      isIntegrationBannerVisible={(adPlatform) => {
        return dependency.showPerformanceReportUsecase.isIntegrationBannerVisible(adPlatform);
      }}
      isRemovedLocalData={state.projectEditing.isRemovedLocalData}
      onClickReorderButton={() => {
        clearRecoilState();
        dependency.editProjectUsecase.createDraftProjectFromExistingOneAndStartEdit({
          organizationId: state.project.project!.organization.id,
          parentProjectId: state.project.project!.id,
        });
      }}
      onChangeTab={(_: any, tabName: string) => history.replace(`/projects/${match.params.projectId}/${tabName}`)}
      // onGotoAnalyticsPage={() => {
      //   dependency.showPerformanceReportUsecase.goCreativeAnalyzeListPage();
      // }}
      onMount={async () => {
        dependency.authenticationUsecase.needSignedIn();
        dependency.showProjectUsecase.prepareProject(match.params.projectId).then(() => {
          dependency.showPerformanceReportUsecase.prepareReporting();
        });
        dependency.integrateTiktokAdAccountUsecase.fetchAccessibleTiktokAdAccounts();
      }}
      onUnmount={() => dependency.showProjectUsecase.disposeProject()}
      onCloseRemoveNotificationDialog={() => dependency.showProjectUsecase.removeLocalDraftProject()}
      onSubmitProjectIssue={(projectId: string, inputComment: string) =>
        dependency.showProjectUsecase.createProjectIssue(projectId, inputComment)
      }
      onSubmitProjectIssueComment={(issueId: string, comment: string) =>
        dependency.showProjectUsecase.createProjectIssueComment(issueId, comment)
      }
      onUpdateProjectIssueComment={(commentId: string, comment: string) =>
        dependency.showProjectUsecase.updateProjectIssueComment(commentId, comment)
      }
      onDeleteProjectIssueComment={(commentId: string) =>
        dependency.showProjectUsecase.deleteProjectIssueComment(commentId)
      }
      reloadProject={() => {
        dependency.showProjectUsecase.prepareProject(match.params.projectId);
      }}
    />
  );
};

export default ProjectDetailPage;
